// ABOUTME: Warehouse orders view of shipped orders finalized in the e-check without their label,
// ABOUTME: where a selection of orders gets its labels printed on the workspace label printer.

import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import PrintIcon from '@mui/icons-material/Print';
import OrdersCard from "../../../../../components/global/cards/orders/OrdersCard";
import { ORDER_FILTER_KEYS, SHIPPED_FILTER_KEYS } from '../../../../../components/global/DataGrid/Filter';
import MPrintedOrderLabels from "../../../../../components/global/Modals/MPrintedOrderLabels";
import DropdownButton from '../../../../../components/theme/buttons/DropdownButton';
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShDataGridSelectedActions from '../../../../../components/theme/datagrid2/ShDataGridSelectedActions';
import ShCopyButton from '../../../../../components/theme/ShCopy';
import SidepanelLoader from '../../../../../components/theme/sidepanel2/SidepanelLoader';
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import Text from "../../../../../components/theme/text/Text";
import { useGetOrdersQuery, usePrintOrderLabelsMutation } from "../../../../../newapi/order/ordersSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { tokens } from "../../../../../theme";
import { UserPrivileges } from '../../../../../utils/permissions';
import { general_states } from '../../../../../utils/staticEnums';
import { showToastMessage, showToastMessageRequestError } from '../../../../../utils/toasts';

const SpOrderDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));

const OutboundLabelPrintPendingView = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const selectedWorkSpace = useSelector((state) => state.settings.workSpace);

    const gridOptions = {
        export:true,
        disableCustomActionsOnPhone: true,
        isSidepanel: true,
        filter: [
            SHIPPED_FILTER_KEYS.STREAM,
            SHIPPED_FILTER_KEYS.SHIPPER,
            ...(isFulfilment ? [SHIPPED_FILTER_KEYS.RELATION] : []),
        ],
    };

    const hasPrintPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MANAGE])
    );

    const [config] = useState({
        [ORDER_FILTER_KEYS.STATE.backend_key]: [general_states.SHIPPED],
        labelPrintPending: true
    });

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
        });

        return () => {};
    },[]);

    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    const [orderId, setOrderId] = useState();
    const orderIdRef = useRef(orderId);

    const getOrderDetails = (params) => {
        if (!params) return;

        const neworderId = parseInt(params.row.id);
        if (orderIdRef.current !== neworderId) {
            if(!spLoading) {
                setSpLoading(true);
            }
            setOrderId(neworderId);
            orderIdRef.current = neworderId;
        }
        setIsExtended(true);
    };

    const [selectedItems, setSelectedItems] = useState([]);
    const onSelected = (locSelectedItems) => {
        setSelectedItems(locSelectedItems);
    };

    const [printResults, setPrintResults] = useState({ open: false, results: [], workspaceId: null });
    const [printOrderLabels, { isLoading: printLoading }] = usePrintOrderLabelsMutation();

    const handlePrintLabels = async () => {
        if (selectedItems.length === 0 || printLoading) return;

        if (!selectedWorkSpace?.id) {
            showToastMessage("error", t("select_workspace_with_label_printer"));
            return;
        }

        try {
            const response = await printOrderLabels({
                orderIds: selectedItems,
                workspaceId: selectedWorkSpace.id
            }).unwrap();

            setSelectedItems([]);
            setPrintResults({ open: true, results: response.results ?? [], workspaceId: selectedWorkSpace.id });
        } catch (error) {
            showToastMessageRequestError(t("label_print_failed"), t, error);
        }
    };

    const columns = [
        {
            field: "barcode",
            headerName: t('order'),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <Box sx={{
                        minWidth:"100%",
                        height:"100%",
                        display:"flex",
                        alignItems:"center",
                        position:"relative",
                        whiteSpace:"nowrap",
                        overflow:"hidden",
                        ".copyel": {
                            opacity:0,
                        },
                        "&:hover .copyel": {
                            opacity:1,
                        }
                    }}>
                        <Text noBreak>{params.row.barcode}</Text>
                        <ShCopyButton
                            styles={{button:{
                                background:theme.palette.mode === "dark" ? colors.grey[600] :  colors.grey[200],
                                color: colors.txt["primary"],
                                "&:hover" : {
                                    background:theme.palette.mode === "dark" ? colors.grey[500] :colors.grey[300],
                                }
                            }}}
                            sx={{position:"absolute",right:0,zIndex:3000}}
                            className={"copyel"}
                            closeAfter={1500}
                            value={params.row.barcode}
                        ></ShCopyButton>
                    </Box>
                );
            }
        },
        {field: "deliveryName", headerName: t("delivery_name"),flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.deliveryName
            }
        },
        {field: "webshopreference", headerName: t("webshopreference"),flex:2, filter: SHIPPED_FILTER_KEYS.RELATION,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (<Text>{`${params.row.relation?.name} - ${params.row.webshopreference} (${params.row.deliveryCountry ? params.row.deliveryCountry.toUpperCase() : ""})`}</Text>)
            }
        },
        {field: "skipReason", headerName: t("reason"),flex:2, sortable:false,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return <Text>{params.row.skipReason}</Text>
            }
        },
        {field: "stream", headerName: t("stream"),flex:1, filter: SHIPPED_FILTER_KEYS.STREAM,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.stream?.shortName
            }
        },
        {field: "shipper", headerName: t("shipper_name"),flex:2, filter: SHIPPED_FILTER_KEYS.SHIPPER,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return `${params.row.shipper?.name ?? ""} - ${params.row.shipperprofile?.name ?? ""}`
            }
        },
        {
            field: "date_sent",
            headerName: t("date_sent"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                if (!params.row.date_sent) {
                    return "";
                }

                const date = new Date(params.row.date_sent);
                const dateStr = date.toLocaleDateString(i18n.language, { dateStyle: 'short' });
                const timeStr = date.toLocaleTimeString(i18n.language, { timeStyle: 'short' });

                return (
                    <Text>
                        {dateStr}{' '}<Text light>{timeStr}</Text>
                    </Text>
                );
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <OrdersCard onClick={() => getOrderDetails(params)} skeleton={params.row.skeleton ?? false} {...params.row}  />
                )
            }
        }
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 key={theme.palette.mode}
                pollingInterval={true}
                content={useGetOrdersQuery}
                config={config}
                title={t('label_print_pending')}
                gridOptions={gridOptions}
                sortModel={{field: "date_sent", type:"DESC"}}
                columns={columns}
                onRowClick={getOrderDetails}
                selectable
                setSelected={onSelected}
                keepSelectedAlive
                keepNonExistentRowsSelected
                selectedItems={selectedItems}
                selectedActions={
                    <ShDataGridSelectedActions selectedItems={selectedItems}>
                        {hasPrintPrivilege && (
                            <DropdownButton
                                onClick={() => handlePrintLabels()}
                                icon={<PrintIcon/>}
                                text={t("print_labels")}
                            />
                        )}
                    </ShDataGridSelectedActions>
                }
                isRowSelectable={() => hasPrintPrivilege}
            >
            </ShDataGrid2>
            {spLoading &&
                <SpOrderDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    orderId={orderId}
                />
            }
            <MPrintedOrderLabels
                open={printResults.open}
                handleClose={() => setPrintResults(prev => ({ ...prev, open: false }))}
                results={printResults.results}
                workspaceId={printResults.workspaceId}
            />
        </Box>
    );
}
export default OutboundLabelPrintPendingView;
